import React, { useEffect, useState, useRef } from 'react';
import { Container, Icon, Button } from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { useSetRecoilState } from 'recoil';
import { chatbotSessionState } from '../../../../models/chatbot/recoil-state';
import { updateChatThread } from '../../../../services/chatbot';
import { AiMarkdown, ReferenceCard } from '../../../ai-content';
import { isLandscapeCard } from '../../../../models/chatbot/types';
import AiResponseActions from '../../../ai-content/AiResponseActions';
import styles from './ai-summary.module.scss';
import { LoadingSkeleton } from '../../loading-skeleton';
import useFeatureFlag from '../../../../common/use-feature-flag';
import { Feature } from '../../../../models/features/features';
import { useAISummaryCache } from './use-ai-summary-cache';
import {
  trackAISummaryExpand,
  trackAISummaryCollapse,
  trackAISummaryDeepDive,
  trackAISummaryReferenceClick,
  trackAISummaryLinkClick,
} from '../../../../models/assistant/analytics/analytics';

const TypingIndicator = ({ label }: { label: string }) => (
  <span
    className={styles.TypingIndicator}
    role="status"
    aria-label={label}
    data-testid="playwright-ai-summary-streaming-indicator"
  >
    <span className={styles.Dot} />
    <span className={styles.Dot} />
    <span className={styles.Dot} />
  </span>
);

interface AISummaryProps {
  query: string;
  programId: string;
}

const AISummary: React.FC<AISummaryProps> = ({ query, programId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const setChatbotSession = useSetRecoilState(chatbotSessionState);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { content, isLoading, threadId, references, copy } = useAISummaryCache(
    query,
    programId
  );
  const chatbotEnabled = useFeatureFlag(Feature.CHATBOT_ENABLED);
  const aiSummaryEnabled = useFeatureFlag(Feature.AI_SEARCH_SUMMARY_ENABLED);

  const hasReferences = references.length > 0;
  const hasLandscapeCards = references.some(
    (ref) => ref.card && isLandscapeCard(ref.card)
  );

  const toggleExpanded = () => {
    const willExpand = !isExpanded;
    setIsExpanded(willExpand);
    if (willExpand) {
      trackAISummaryExpand({ query, thread_id: threadId });
    } else {
      trackAISummaryCollapse({ query, thread_id: threadId });
    }
  };

  // Check for overflow when content or references change (including during streaming).
  // Skip when expanded — the content div has no max-height so scrollHeight === offsetHeight,
  // which would incorrectly reset hasOverflow to false.
  useEffect(() => {
    if (contentRef.current && !isExpanded) {
      const element = contentRef.current;
      const isOverflowing = element.scrollHeight > element.offsetHeight;
      // During streaming, only latch true (never revert to false) to prevent
      // oscillation caused by the typing indicator moving in/out of the content div.
      // When streaming ends, re-evaluate cleanly without the indicator present.
      if (isOverflowing || !isLoading) {
        setHasOverflow(isOverflowing);
      }
    }
  }, [content, isLoading, references.length, isExpanded]);

  // Reset expand/overflow state when query changes
  useEffect(() => {
    setIsExpanded(false);
    setHasOverflow(false);
  }, [query]);

  const handleDeepDive = () => {
    if (threadId) {
      trackAISummaryDeepDive({ query, thread_id: threadId });
      // Convert the search-originated thread to a chat thread so it appears
      // in the "Chats" tab of the history panel going forward.
      updateChatThread(threadId, { source: 'chat' }).catch(() => {});
      setChatbotSession({
        threadId,
        isThreadPersisted: true,
      });
      history.push('/assistant');
    }
  };

  // Don't show anything if no query
  if (!query) {
    return null;
  }

  const generatingLabel = t('search.ai_summary.generating');

  return (
    <Container
      shadow="none"
      shape="large"
      variant="default"
      className={cx(styles.AISummaryContainer, isLoading && styles.Loading)}
      role="region"
      aria-label={t('search.ai_summary.title')}
      aria-busy={isLoading}
      {...(aiSummaryEnabled ? { 'data-pendo-id': 'ai-summary-container' } : {})}
    >
      <div className={styles.Header}>
        <div className={styles.TitleContainer}>
          <h2 className={styles.Title}>{t('search.ai_summary.title')}</h2>
        </div>

        {isLoading && content && hasOverflow && (
          <TypingIndicator label={generatingLabel} />
        )}
      </div>

      {isLoading && !content ? (
        <div
          className={styles.LoadingContent}
          data-testid="playwright-ai-summary-loading"
        >
          <p className={styles.LoadingStatus} role="status">
            {t('search.ai_summary.generating_summary')}
          </p>
          <div className={styles.LoadingParagraph}>
            <LoadingSkeleton
              width="100%"
              height="12px"
              marginTop="0"
              variant="contrast"
            />
            <LoadingSkeleton
              width="98%"
              height="12px"
              marginTop="0"
              variant="contrast"
            />
            <LoadingSkeleton
              width="100%"
              height="12px"
              marginTop="0"
              variant="contrast"
            />
            <LoadingSkeleton
              width="64%"
              height="12px"
              marginTop="0"
              variant="contrast"
            />
          </div>
          <div className={styles.LoadingReference}>
            <LoadingSkeleton
              width="85px"
              height="48px"
              borderRadius={4}
              marginTop="0"
              variant="contrast"
            />
            <div className={styles.LoadingReferenceText}>
              <LoadingSkeleton
                width="78%"
                height="13px"
                marginTop="0"
                variant="contrast"
              />
              <LoadingSkeleton
                width="46%"
                height="11px"
                marginTop="8px"
                variant="contrast"
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            ref={contentRef}
            className={cx(
              styles.Content,
              !isExpanded && hasOverflow && styles.Collapsed,
              isLoading && !isExpanded && styles.Loading
            )}
            data-testid="playwright-ai-summary-content"
          >
            <AiMarkdown
              content={content}
              onLinkClick={(url: string, is_internal: boolean) =>
                trackAISummaryLinkClick({
                  query,
                  thread_id: threadId,
                  url,
                  is_internal,
                })
              }
            />
            {hasReferences && (
              <nav
                className={`ai-content__references ${
                  hasLandscapeCards
                    ? 'ai-content__references--has-landscape'
                    : ''
                }`}
                aria-label={t('chatbot.references.title')}
              >
                {references.map((reference) => (
                  <ReferenceCard
                    key={`${reference.referenceType}-${reference.referenceId}`}
                    referenceType={reference.referenceType}
                    referenceId={reference.referenceId}
                    card={reference.card}
                    onClick={() =>
                      trackAISummaryReferenceClick({
                        query,
                        thread_id: threadId,
                        reference_type: reference.referenceType,
                        reference_id: reference.referenceId,
                      })
                    }
                  />
                ))}
              </nav>
            )}
            {isLoading && !hasOverflow && (
              <TypingIndicator label={generatingLabel} />
            )}
          </div>
          {!isLoading && (isExpanded || !hasOverflow) && content && (
            <div className={styles.ContentActions}>
              <AiResponseActions
                copy={copy}
                isStreaming={isLoading}
                {...(aiSummaryEnabled
                  ? { pendoId: 'ai-summary-response-actions' }
                  : {})}
              />
            </div>
          )}
          {!isLoading && (isExpanded || !hasOverflow) && content && (
            <p className={styles.Disclaimer}>{t('chatbot.disclaimer')}</p>
          )}
          {((!isLoading && threadId && chatbotEnabled) ||
            hasOverflow ||
            isExpanded) && (
            <div
              className={`${styles.FooterRow} ${
                !isLoading && threadId && chatbotEnabled
                  ? styles.FooterRowWithButton
                  : ''
              }`}
            >
              {!isLoading && threadId && chatbotEnabled && (
                <Button
                  variant="outline"
                  size="compact"
                  onClick={handleDeepDive}
                  label={t('search.ai_summary.search_with_assistant')}
                  leftIcon={
                    <Icon className={styles.AssistantButtonIcon} filled>
                      auto_awesome
                    </Icon>
                  }
                  className={styles.SearchWithAssistantButton}
                  {...(chatbotEnabled
                    ? { 'data-pendo-id': 'ai-summary-deep-dive-button' }
                    : {})}
                />
              )}
              {(hasOverflow || isExpanded) && (
                <Button
                  variant="text"
                  size="compact"
                  onClick={toggleExpanded}
                  rightIcon={
                    <Icon>{isExpanded ? 'expand_less' : 'expand_more'}</Icon>
                  }
                  label={
                    isExpanded
                      ? t('search.ai_summary.see_less')
                      : t('search.ai_summary.see_more')
                  }
                  className={styles.SeeMoreButton}
                />
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default AISummary;
